import { useTranslation } from "react-i18next";
import { Confetti } from "@/components/illustrations/Confetti";
import { KLSkyline } from "@/components/illustrations/KLSkyline";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { absoluteUrl, pageTitle } from "@/seo/site";
import { organizationLd, sportsEventLd, webPageLd } from "@/seo/structuredData";
import { RaceDaySectionLed } from "@/pages/raceDay/RaceDaySectionLed";
import { BrandHeadingSuffix } from "@/components/seo/BrandHeadingSuffix";

const RACE_DAY_DESCRIPTION =
  "Your race-day guide for the Bursa Bull Charge (BBC) × The Edge KL Rat Race 2026 — venue map, evening programme, and accessible transport. Saturday 28 November 2026 at Bursa Malaysia, Exchange Square, Kuala Lumpur.";

export default function RaceDayPage() {
  useDocumentMeta({
    title: pageTitle("Race Details"),
    description: RACE_DAY_DESCRIPTION,
    canonicalUrl: absoluteUrl("/race-details/"),
    jsonLd: [
      organizationLd(),
      // This is the page that should answer "what time / where / what route is the
      // KL rat race", so it carries the event entity too. The stable @id
      // (${SITE_URL}/#event) means this is the SAME node as the home page's, not a
      // duplicate event.
      sportsEventLd(),
      webPageLd({
        name: "Race Details",
        description: RACE_DAY_DESCRIPTION,
        path: "/race-details/",
        breadcrumb: [
          { name: "Home", path: "/" },
          { name: "Race Details", path: "/race-details/" },
        ],
      }),
    ],
  });

  return (
    <>
      <PageHero />
      <RaceDaySectionLed />
    </>
  );
}

function PageHero() {
  const { t } = useTranslation("raceday");

  return (
    <section className="relative overflow-hidden bg-hero-sky text-surface-deep-foreground">
      {/*
        * Poster-faithful overlay (light): faint multi-colour confetti texture
        * + KL skyline silhouette at the foot of the hero. Magenta eyebrow
        * carries the race-night / dinner-phase celebration tone already
        * used on the programme timeline below.
        */}
      <Confetti className="absolute inset-0 opacity-50" density="low" />
      {/*
       * Skyline parks in the right half on tablet+ so the Petronas Twin
       * Towers (centered by KLSkyline's `preserveAspectRatio="xMidYEnd
       * meet"`) sit safely to the right of the text column instead of
       * cutting across the headline. Hidden on mobile — the narrow section
       * can't fit text + illustration side-by-side without the towers
       * crashing into the body copy. The Confetti overlay above carries
       * the ambient celebration texture on every viewport.
       */}
      <KLSkyline
        className="absolute bottom-0 right-0 hidden h-40 w-1/2 md:block lg:h-48"
        toneClassName="text-surface-deep-foreground/12"
      />

      <div className="container relative py-16 md:py-24">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] text-accent-magenta font-semibold">{t("hero.eyebrow")}</p>
          <h1 className="mt-3 text-4xl md:text-6xl">
            {t("hero.heading")}
            <BrandHeadingSuffix />
          </h1>
          <p className="mt-4 text-surface-deep-foreground/80">
            {t("hero.body")}
          </p>
        </div>
      </div>
    </section>
  );
}
